"use client"

import { useEffect, useRef, useState } from "react"

type Turnstile = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      action: string
      theme: "light"
      size: "flexible" | "compact"
      "response-field": false
      retry: "never"
      "refresh-expired": "manual"
      "refresh-timeout": "auto"
      callback: (token: string) => void
      "expired-callback": () => void
      "error-callback": (code: string) => void
      "timeout-callback": () => void
      "unsupported-callback": () => void
      "before-interactive-callback": () => void
    },
  ) => string
  remove: (id: string) => void
}

declare global {
  interface Window {
    turnstile?: Turnstile
    contactTurnstileReady?: boolean
    onContactTurnstileLoad?: () => void
  }
}

type Status =
  | "checking"
  | "recovering"
  | "interactive"
  | "verified"
  | "expired"
  | "failed"
export type ChallengeFailure =
  | "script"
  | "timeout"
  | "widget"
  | "unsupported"
  | "render"
const MESSAGES: Record<Status, string> = {
  checking: "送信前の確認中です。入力を続けてお待ちいただけます。",
  recovering:
    "確認をやり直しています。入力内容は保持されています。そのままお待ちください。",
  interactive: "下の確認欄のチェックボックスを押してください。",
  verified: "確認が完了しました。送信できます。",
  expired:
    "確認の有効期限が切れました。入力内容は保持されています。送信ボタンを押すと、もう一度確認してから送信します。",
  failed:
    "送信前の確認を完了できませんでした。「確認欄を再読み込み」を押してください。改善しない場合は、SafariやChromeで開き直すか、LINEからご相談ください。入力内容は保持されています。",
}

export function ContactChallenge({
  siteKey,
  onToken,
  onFailure,
  onExpired,
}: {
  siteKey: string
  onToken: (token: string) => void
  onFailure?: (reason: ChallengeFailure, code?: string) => void
  onExpired?: () => void
}) {
  const container = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<Status>("checking")
  useEffect(() => {
    let widgetId: string | undefined
    let widgetSize: "flexible" | "compact" | undefined
    let active = true
    let generation = 0
    let timeout: ReturnType<typeof setTimeout> | undefined
    let checkingNow = false
    let waitDuration = 30000
    let failed = false
    let recoveryCount = 0
    let recoveryPending = false
    let recoveryTimer: ReturnType<typeof setTimeout> | undefined
    const script =
      window.turnstile && window.contactTurnstileReady
        ? undefined
        : (document.querySelector<HTMLScriptElement>(
            "script[data-contact-turnstile]",
          ) ?? document.createElement("script"))
    const showError = (reason: ChallengeFailure, code?: string) => {
      if (!active) return
      clearTimeout(recoveryTimer)
      recoveryPending = false
      setStatus("failed")
      if (failed) return
      failed = true
      checkingNow = false
      clearTimeout(timeout)
      if (!window.contactTurnstileReady) script?.remove()
      onToken("")
      onFailure?.(reason, code)
    }
    const scriptError = () => showError("script")
    const armTimeout = () => {
      clearTimeout(timeout)
      // バックグラウンドやオフラインではブラウザーが通信を止めるため、失敗と判定しない。
      if (!active || !checkingNow || document.hidden || !navigator.onLine)
        return
      timeout = setTimeout(() => showError("timeout"), waitDuration)
    }
    const checking = (duration = 60000) => {
      if (!active) return
      clearTimeout(recoveryTimer)
      recoveryPending = false
      failed = false
      checkingNow = true
      waitDuration = duration
      setStatus("checking")
      onToken("")
      armTimeout()
    }
    const resume = () => {
      armTimeout()
      clearTimeout(recoveryTimer)
      if (!active || !recoveryPending || document.hidden || !navigator.onLine)
        return
      recoveryTimer = setTimeout(() => {
        if (!active || !recoveryPending || document.hidden || !navigator.onLine)
          return
        recoveryPending = false
        mount(true)
      }, 8000)
    }
    const mount = (recreate = false) => {
      const api = window.turnstile
      if (!active) return
      if (!api || !container.current) {
        showError("render")
        return
      }
      // flexibleにも最小幅300pxがあるため、狭いフォームではcompactを使う。
      const size = container.current.clientWidth < 300 ? "compact" : "flexible"
      if (!recreate && widgetId && widgetSize === size) return
      generation += 1
      const mountedGeneration = generation
      const current = () => active && generation === mountedGeneration
      widgetSize = size
      checking()
      try {
        if (widgetId) api.remove(widgetId)
        widgetId = api.render(container.current, {
          sitekey: siteKey,
          action: "contact",
          theme: "light",
          size,
          "response-field": false,
          // SDK側の同じ確認欄での無制限リトライと重ねず、再生成は一度だけにする。
          retry: "never",
          // 長時間の入力中に同じ確認欄の自動更新を繰り返さず、次の送信時に再生成する。
          "refresh-expired": "manual",
          "refresh-timeout": "auto",
          callback: (token) => {
            if (!current()) return
            clearTimeout(timeout)
            clearTimeout(recoveryTimer)
            recoveryPending = false
            checkingNow = false
            failed = false
            setStatus("verified")
            onToken(token)
          },
          "expired-callback": () => {
            if (!current()) return
            clearTimeout(timeout)
            clearTimeout(recoveryTimer)
            recoveryPending = false
            checkingNow = false
            failed = false
            setStatus("expired")
            onToken("")
            onExpired?.()
          },
          "timeout-callback": () => {
            if (current()) checking()
          },
          "error-callback": (code) => {
            if (!current() || failed) return
            showError("widget", code)
            // 設定不一致などは再生成で直らない。公式に再試行可能なコードだけ扱う。
            if (
              recoveryCount < 1 &&
              /^(?:300\d{3}|600\d{3}|200500|110600|110620)$/.test(code)
            ) {
              recoveryCount += 1
              recoveryPending = true
              setStatus("recovering")
              resume()
            }
          },
          "unsupported-callback": () => {
            if (current()) showError("unsupported")
          },
          "before-interactive-callback": () => {
            if (!current()) return
            clearTimeout(timeout)
            clearTimeout(recoveryTimer)
            recoveryPending = false
            failed = false
            checkingNow = false
            setStatus("interactive")
            onToken("")
          },
        })
      } catch {
        showError("render")
      }
    }
    const ready = () => {
      if (!active) return
      const api = window.turnstile
      if (!api) {
        showError("script")
        return
      }
      mount()
    }
    // SDKが初期化を終えた後に呼ぶ正式なonloadコールバックを使う。
    // DOMのloadイベントはSDK内部の初期化完了を保証しない。
    // eslint-disable-next-line functional/immutable-data -- 画面遷移中も共有するSDKの完了通知
    window.onContactTurnstileLoad = () => {
      // eslint-disable-next-line functional/immutable-data -- SDK完了後だけ再利用を許可する
      window.contactTurnstileReady = true
      window.dispatchEvent(new Event("contact-turnstile-ready"))
    }
    window.addEventListener("contact-turnstile-ready", ready)
    checking(30000)
    document.addEventListener("visibilitychange", resume)
    window.addEventListener("online", resume)
    window.addEventListener("offline", resume)
    if (script) {
      // 読込失敗をキャッシュする next/script を避け、再試行ごとに読み込む。
      script.setAttribute(
        "src",
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onContactTurnstileLoad",
      )
      script.setAttribute("async", "")
      script.setAttribute("data-contact-turnstile", "")
      script.addEventListener("error", scriptError)
      if (!script.isConnected) document.head.append(script)
    } else ready()
    const observer = new ResizeObserver(() => {
      if (widgetId) mount()
    })
    if (container.current) observer.observe(container.current)
    return () => {
      active = false
      clearTimeout(timeout)
      clearTimeout(recoveryTimer)
      recoveryPending = false
      observer.disconnect()
      document.removeEventListener("visibilitychange", resume)
      window.removeEventListener("online", resume)
      window.removeEventListener("offline", resume)
      window.removeEventListener("contact-turnstile-ready", ready)
      script?.removeEventListener("error", scriptError)
      // 読込中の画面切替でもスクリプトを共有し、SDKの二重読込を避ける。
      if (widgetId) window.turnstile?.remove(widgetId)
    }
  }, [siteKey, onToken, onFailure, onExpired])
  return (
    <>
      <p
        id="contact-challenge-status"
        role={status === "failed" ? "alert" : "status"}
        aria-live="polite"
      >
        {MESSAGES[status]}
        {status === "failed" && (
          <>
            {" "}
            <a
              href="https://line.me/R/ti/p/@217onxfu"
              target="_blank"
              rel="noopener noreferrer"
            >
              LINEで相談する（別タブ）
            </a>
          </>
        )}
      </p>
      <div ref={container} />
    </>
  )
}
